/**
 * AuditSite component.
 * @module components/AuditSite/AuditSite
 */
import React, { useEffect } from 'react';
import { getUser } from '@plone/volto/actions/users/users';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import { asyncConnect } from '@plone/volto/helpers/AsyncConnect';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import jwtDecode from 'jwt-decode';
import { createPortal } from 'react-dom';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Container, Loader, Message, Table } from 'semantic-ui-react';

import { getSiteAudit } from '../../actions';

import backSVG from '@plone/volto/icons/back.svg';

const messages = defineMessages({
  scriptAudit: {
    id: 'ScriptAudit',
    defaultMessage: 'Script Audit',
  },
  back: {
    id: 'Back',
    defaultMessage: 'Back',
  },
  loading: {
    id: 'Loading...',
    defaultMessage: 'Loading...',
  },
  error: {
    id: 'Error loading script audit',
    defaultMessage: 'Error loading script audit',
  },
  noResults: {
    id: 'No results found',
    defaultMessage: 'No results found',
  },
  timestamp: {
    id: 'Date/Time',
    defaultMessage: 'Date/Time',
  },
  username: {
    id: 'Username',
    defaultMessage: 'Username',
  },
  action: {
    id: 'Action',
    defaultMessage: 'Action',
  },
  actionUpdated: {
    id: 'site_audit.action.updated',
    defaultMessage: 'Updated',
  },
  script: {
    id: 'Script',
    defaultMessage: 'Script',
  },
});

/**
 * AuditSite component.
 * @function AuditSite
 * @returns {JSX.Element} Markup of the component.
 */
const AuditSite = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();

  const { items, loaded, loading, error } = useSelector(
    (state) => state.siteAudit,
  );

  useEffect(() => {
    if (!loaded && !loading && !error) dispatch(getSiteAudit());
  }, [dispatch, loaded, loading, error]);

  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('pt-BR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return timestamp;
    }
  };

  const isClient = typeof window !== 'undefined';

  return (
    <div id="page-audit-site">
      <Helmet title={intl.formatMessage(messages.scriptAudit)} />
      <Container>
        <h1 className="documentFirstHeading">
          {intl.formatMessage(messages.scriptAudit)}
        </h1>
        {loading && <Loader active inline="centered" />}
        {error && (
          <Message negative>{intl.formatMessage(messages.error)}</Message>
        )}
        {loaded && items.length === 0 && (
          <Message info>{intl.formatMessage(messages.noResults)}</Message>
        )}
        {loaded && items.length > 0 && (
          <Table celled striped>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>
                  {intl.formatMessage(messages.timestamp)}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {intl.formatMessage(messages.username)}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {intl.formatMessage(messages.action)}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {intl.formatMessage(messages.script)}
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {items.map((item) => (
                <Table.Row key={item.id}>
                  <Table.Cell>{formatTimestamp(item.timestamp)}</Table.Cell>
                  <Table.Cell>{item.username}</Table.Cell>
                  <Table.Cell>
                    {intl.formatMessage({
                      id: `site_audit.action.${item.action}`,
                      defaultMessage: item.action,
                    })}
                  </Table.Cell>
                  <Table.Cell>
                    <code className="audit-script-code">{item.value}</code>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Container>
      {isClient &&
        typeof document !== 'undefined' &&
        document.getElementById('toolbar') &&
        createPortal(
          <Toolbar
            pathname="/log/audit-site"
            hideDefaultViewButtons
            inner={
              <button
                className="back"
                aria-label={intl.formatMessage(messages.back)}
                onClick={() => history.goBack()}
              >
                <Icon
                  name={backSVG}
                  className="circled"
                  size="30px"
                  title={intl.formatMessage(messages.back)}
                />
              </button>
            }
          />,
          document.getElementById('toolbar'),
        )}
    </div>
  );
};

const asyncItems = [
  {
    key: 'user',
    promise: ({ store }) => {
      try {
        const token = store.getState().userSession?.token;
        if (token) {
          const userId = jwtDecode(token).sub;
          return store.dispatch(getUser(userId));
        }
      } catch (e) {
        return null;
      }
      return null;
    },
  },
  {
    key: 'siteAudit',
    promise: ({ store }) => store.dispatch(getSiteAudit()),
  },
];

export default asyncConnect(asyncItems)(AuditSite);
